<template>
  <section id="signup-form" class="container">
    <div class="signup-wrapper">
      <section class="form-wrapper single-column">
        <h2>Sign Up</h2>
        <div class="error-notification" v-if="auth0error">{{ auth0error }}</div>
        <form id="register-form" v-on:submit.prevent="onSubmit" novalidate>
          <div class="side-input-wrapper">
            <fieldset class="side-input">
              <div class="form-label">
                <label for="username">Username</label>
                <div
                  class="error-message"
                  v-if="!$v.username.required && $v.username.$dirty"
                >Field required</div>
                <div
                  class="error-message"
                  v-if="!$v.username.wellFormatted && $v.username.$dirty"
                >Please note that only letters, numbers, - and _ are allowed in your username, and it must start with a letter or number and at least 3 character long</div>
              </div>
              <input
                type="text"
                name="username"
                v-bind:class="{ error: $v.username.$error && $v.username.$dirty }"
                v-model.trim.lazy="$v.username.$model"
                placeholder="username"
                autocomplete="off"
                aria-label="username"
                maxlength="32"
              />
            </fieldset>
            <fieldset class="side-input">
              <div class="form-label">
                <label for="email">Email</label>
                <div
                  class="error-message"
                  v-if="!$v.email.required && $v.email.$dirty"
                >Field required</div>
                <div
                  class="error-message"
                  v-if="!$v.email.email && $v.email.$dirty"
                >Must be a valid email</div>
              </div>
              <input
                type="text"
                name="email"
                v-bind:class="{ error: $v.email.$error && $v.email.$dirty }"
                v-model.trim.lazy="$v.email.$model"
                placeholder="email"
                aria-label="email"
              />
            </fieldset>
            <fieldset class="container-password-field side-input">
              <div class="form-label">
                <label for="password">Password</label>
                <div
                  class="error-message"
                  v-if="!$v.password.required && $v.password.$dirty"
                >Field required</div>
                <div
                  class="error-message"
                  v-if="!$v.password.minLength && $v.password.$dirty"
                >Password must be at least 8 characters long</div>
              </div>
              <div class="input-container">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  name="password"
                  v-bind:class="{ error: $v.password.$error && $v.password.$dirty }"
                  v-model.trim.lazy="$v.password.$model"
                  placeholder="PASSWORD (min 8 characters)"
                  class="signin-password"
                  autocomplete="off"
                  aria-label="password"
                  @keyup="showEyeButtons($event, 'first')"
                />
                <div class="show-password first">
                  <button
                    class="show-pass"
                    type="button"
                    tabindex="-1"
                    v-show="!showPassword"
                    v-on:click="showPassword = true"
                  ></button>
                  <button
                    class="hide-pass"
                    type="button"
                    tabindex="-1"
                    v-show="showPassword"
                    v-on:click="showPassword = false"
                  ></button>
                </div>
              </div>
            </fieldset>
            <fieldset class="container-password-field side-input">
              <div class="form-label">
                <label for="password-confirm">Confirm password</label>
                <div
                  class="error-message"
                  v-if="!$v.confirmPassword.required && $v.confirmPassword.$dirty"
                >Field required</div>
                <div
                  class="error-message"
                  v-if="!$v.confirmPassword.sameAs && $v.confirmPassword.$dirty"
                >password mismatch</div>
              </div>
              <div class="input-container">
                <input
                  :type="showConfirmPassword ? 'text' : 'password'"
                  name="password-confirm"
                  v-bind:class="{ error: $v.confirmPassword.$error && $v.confirmPassword.$dirty }"
                  v-model.trim.lazy="$v.confirmPassword.$model"
                  placeholder="confirm password"
                  class="signin-password"
                  autocomplete="off"
                  minlength="8"
                  aria-label="password confirm"
                  @keyup="showEyeButtons($event, 'confirm')"
                />
                <div class="show-password confirm">
                  <button
                    class="show-pass"
                    type="button"
                    tabindex="-1"
                    v-show="!showConfirmPassword"
                    v-on:click="showConfirmPassword = true"
                  ></button>
                  <button
                    class="hide-pass"
                    type="button"
                    tabindex="-1"
                    v-show="showConfirmPassword"
                    v-on:click="showConfirmPassword = false"
                  ></button>
                </div>
              </div>
            </fieldset>
          </div>
          <section class="privacy-options">
            <div class="form-label">
              <div
                class="error-message"
                v-if="!$v.privacyApproval.sameAs && $v.privacyApproval.$dirty"
              >Please accept the privacy policy to continue</div>
            </div>
            <label class="my-checkbox">
              <input
                type="checkbox"
                name="privacy_approval"
                v-model.lazy="$v.privacyApproval.$model"
              />
              <span
                class="custom-checkbox"
                v-bind:class="{ error: $v.privacyApproval.$error && $v.privacyApproval.$dirty }"
              ></span>
              <span>
                I confirm to have read the
                <span class="asterisk-wrapper">
                  <a href="https://www.arduino.cc/PrivacyPolicy/en/" target="_blank">
                    privacy
                    policy
                  </a>
                  <span class="required-asterisk">*</span>
                </span> and to accept the
                <span class="asterisk-wrapper">
                  <a
                    href="https://www.arduino.cc/en/Main/TermsOfService"
                    target="_blank"
                  >Terms of service</a>
                  <span class="required-asterisk">*</span>
                </span>
              </span>
            </label>
            <label class="my-checkbox">
              <input type="checkbox" name="newsletter_approval" v-model="newsletterApproval" />
              <span class="custom-checkbox"></span>
              <span>I confirm my consent to receive your newsletter</span>
            </label>
            <label class="my-checkbox">
              <input type="checkbox" name="marketing_approval" v-model="marketingApproval" />
              <span class="custom-checkbox"></span>
              <span>
                I confirm my consent to the processing of my personal data for marketing purposes,
                consisting in commercial offers sent via email
              </span>
            </label>
            <label class="my-checkbox">
              <input type="checkbox" name="tracking_approval" v-model="trackingApproval" />
              <span class="custom-checkbox"></span>
              <span>
                I confirm my consent to automated processing of my personal data, by means of profiling,
                in order to receive commercial offers customized on the basis of my browsing and purchasing
                behavior
              </span>
            </label>
          </section>
          <div class="cta-wrapper">
            <input
              type="submit"
              value="Create Account"
              class="btn"
              v-bind:class="{ busy: loading }"
            />
          </div>
        </form>
      </section>
    </div>
  </section>
</template>

<script>
import authService from '../auth/authService';
import { required, email, minLength, sameAs } from 'vuelidate/lib/validators';

const wellFormatted = value => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return true;
  }
  return /^[a-z0-9][a-z0-9_\-]{2,}$/gi.test(value); //eslint-disable-line
};

export default {
  name: 'Register',
  data () {
    return {
      username: null,
      email: null,
      password: null,
      confirmPassword: null,
      privacyApproval: false,
      newsletterApproval: false,
      marketingApproval: false,
      trackingApproval: false,
      auth0error: null,
      loading: false,
      showPassword: false,
      showConfirmPassword: false
    };
  },
  validations: {
    username: {
      required,
      wellFormatted
    },
    email: {
      required,
      email
    },
    password: {
      minLength: minLength(8),
      required
    },
    confirmPassword: {
      sameAs: sameAs('password'),
      required
    },
    privacyApproval: {
      sameAs: sameAs(() => true)
    }
  },
  methods: {
    onSubmit () {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.auth0error = null;
        this.loading = true;

        const params = {
          username: this.username,
          email: this.email,
          password: this.password,
          user_metadata: {
            privacy_approval: this.privacyApproval.toString(),
            newsletter_approval: this.newsletterApproval.toString(),
            marketing_approval: this.marketingApproval.toString(),
            tracking_approval: this.trackingApproval.toString()
          }
        };

        authService.signupAndLogin(params, this.onRegisterErrorCallback);
      }
    },
    onRegisterErrorCallback (error) {
      this.loading = false;
      this.auth0error = error.description;
    },
    showEyeButtons (event, input) {
      if (event.target.value) {
        document.querySelector(`.show-password.${input}`).classList.add('show');
      } else {
        document
          .querySelector(`.show-password.${input}`)
          .classList.remove('show');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.side-input-wrapper {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  .side-input {
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;
    width: calc(50% - 30px);

    @include breakpoint(950) {
      margin-bottom: 0;
      width: 100%;
    }

    .form-label {
      flex-grow: 1;
    }
  }
}

.privacy-options {
  background-color: rgba($white, 0.5);
  border: 1px solid rgba($fog, 0.8);
  font-size: 13px;
  line-height: 1.45;
  margin: 20px 0 40px;
  padding: 25px 30px 30px 28px;

  .required-asterisk {
    display: inline-block;
    margin-left: 2px;
  }

  .asterisk-wrapper {
    white-space: nowrap;
  }

  .my-checkbox {
    @extend %ardu-checkbox;
    align-items: flex-start;
    margin-bottom: 15px;

    &:last-child {
      margin-bottom: 0;
    }

    .custom-checkbox {
      margin-right: 13px;

      &.error {
        border: 1px solid $notifyerror;
      }
    }

    a {
      @extend %text-link;
    }
  }
}
</style>
